import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import useApi from "../hooks/useApi";
import { useNotifications } from "../context/NotificationProvider";
import { useTranslation } from 'react-i18next';
import { useTranslatableData } from "../hooks/useTranslatable";
import { Map } from './google_map_flypro';
import { useLoadScript } from '@react-google-maps/api';
import UserService from "../services/user.service";
import MenuHeader from "./MenuHeader";
import useAuth from '../hooks/useAuth';
import BookingService from "../services/booking.service";
import { bindEvent, getPusher, initPusher } from "../services/pusher.service";

const googleMapsLibraries = ['places'];

const DetailsBooking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const { auth } = useAuth();
  const notify = useNotifications();
  const { booking: rawBooking, therapist: initialTherapist, products: rawProducts } = location.state || {};
  console.log('DetailsBooking location state:', rawProducts);
  const [rawBookingState, setRawBookingState] = useState(rawBooking);
  const [rawProductsState, setRawProductsState] = useState(rawProducts);
  const booking = useTranslatableData(rawBookingState);
  const products = useTranslatableData(rawProductsState);
  const [therapist, setTherapist] = useState(initialTherapist);
  const [coordinates, setCoordinates] = useState([]);
  const [routeMarkers, setRouteMarkers] = useState([]);
  const refreshInFlightRef = useRef(false);
  const notifyRef = useRef(notify);
  const navigateRef = useRef(navigate);
  const refreshBookingRef = useRef(null);
  const { t, i18n } = useTranslation('commande_details');
  const { isLoaded: isMapLoaded, loadError: mapLoadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: googleMapsLibraries,
  });

  console.log({ booking, therapist, products })

  const formatDateToZurichTime = (date, durationInMinutes = 0, language = 'fr') => {
    const startDate = new Date(date);
    const endDate = new Date(startDate.getTime() + durationInMinutes * 60000);

    const locales = {
      fr: 'fr',
      en: 'en-US'
    };

    const dateFormatter = new Intl.DateTimeFormat(locales[language], {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/Zurich',
    });

    const timeFormatter = new Intl.DateTimeFormat(locales[language], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: language === 'en',
      timeZone: 'Europe/Zurich',
    });

    const datePart = dateFormatter.format(startDate);

    const formatTime = (time) => {
      let formatted = timeFormatter.format(time);
      if (language === 'fr') {
        // Replace "08:30" with "8h30"
        formatted = formatted.replace(':', 'h');
        // Remove leading zero for hour if any
        formatted = formatted.replace(/^0/, '');
      }
      return formatted;
    };

    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);

    const timeSeparator = language === 'fr' ? 'à' : 'to';

    return `${datePart} | ${startTime} ${timeSeparator} ${endTime}`;
  };




  const totalDuration = products?.reduce(
    (sum, prod) => sum + Number(prod?.duration || 0),
    0
  );

  useEffect(() => {
    if (booking?.address?.latitude && booking?.address?.longitude && therapist?.latitude && therapist?.longitude && booking.status === 'IN_TRANSIT') {

      setCoordinates([{ lat: booking.address.latitude, lng: booking.address.longitude }, { lat: therapist.latitude, lng: therapist.longitude }]);
      setRouteMarkers([{ lat: therapist.latitude, lng: therapist.longitude }, { lat: booking.address.latitude, lng: booking.address.longitude }]);
      console.log('Updated Map routeMarkers coordinates:', { coordinates, routeMarkers });

    } else if (booking.status !== 'IN_TRANSIT') {

      setCoordinates([{ lat: booking.address.latitude, lng: booking.address.longitude }]);
      setRouteMarkers([]);

    }

  }, [booking, therapist]);

  useEffect(() => {
    if (rawBookingState?.products) {
      setRawProductsState(rawBookingState.products);
    }
  }, [rawBookingState?.products]);

  useEffect(() => {
    let interval;

    if (therapist?.id) {
      fetchAndUpdateTherapist(); // Initial fetch

      interval = setInterval(() => {
        fetchAndUpdateTherapist();
      }, 30000); // Every 30 sec
    }

    return () => clearInterval(interval);
  }, [therapist?.id]);

  const refreshBooking = useCallback(async () => {
    if (!rawBookingState?.id) return;
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;

    try {
      const response = await BookingService.index({
        with: 'address,therapists.media,therapists.categories,client,products.category,ratings,hotel,transactions',
        id: `in:${rawBookingState.id}`,
        per_page: 1,
      }, api);

      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      const updated = list[0];
      if (!updated) {
        notify.warn("Booking not available.");
        navigate("/commande");
        return;
      }

      setRawBookingState(updated);
      if (updated.products) {
        setRawProductsState(updated.products);
      }
      const nextTherapist =
        (Array.isArray(updated.therapists) && updated.therapists[0]) ||
        updated.therapist ||
        initialTherapist;
      if (nextTherapist) {
        setTherapist(nextTherapist);
      }
    } catch (error) {
      console.error("Realtime booking refresh failed:", error);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [api, rawBookingState?.id, initialTherapist, notify, navigate]);

  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    refreshBookingRef.current = refreshBooking;
  }, [refreshBooking]);

  useEffect(() => {
    const bookingId = rawBookingState?.id;
    if (!bookingId) return;

    initPusher({ token: auth?.accessToken });

    const channel = `private-booking.${bookingId}`;
    const handler = (data) => {
      const payload =
        typeof data === "string"
          ? (() => {
              try {
                return JSON.parse(data);
              } catch (_) {
                return {};
              }
            })()
          : data || {};

      const action = payload?.action?.toString()?.toLowerCase() || "updated";
      if (action === "deleted") {
        notifyRef.current?.warn("Booking deleted.");
        navigateRef.current?.("/commande");
        return;
      }

      refreshBookingRef.current?.();
    };
    const unbind = bindEvent(channel, "booking.updated", handler);

    return () => {
      unbind();
    };
  }, [rawBookingState?.id, auth?.accessToken]);

  useEffect(() => {
    const bookingId = rawBookingState?.id;
    if (!bookingId) return;

    initPusher({ token: auth?.accessToken });
    const pusher = getPusher();
    if (!pusher) return;

    const handleConnected = () => {
      refreshBookingRef.current?.();
    };

    pusher.connection.bind("connected", handleConnected);

    return () => {
      pusher.connection.unbind("connected", handleConnected);
    };
  }, [rawBookingState?.id, auth?.accessToken]);

  useEffect(() => {
    const bookingId = rawBookingState?.id;
    if (!bookingId) return;

    const handleOnline = () => {
      refreshBookingRef.current?.();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [rawBookingState?.id]);



  const handleGoBack = () => {
    navigate(-1);
  };

  const fetchAndUpdateTherapist = async () => {
    try {
      const therapistResponse = await UserService.indexByRole(
        { id: therapist.id, with: 'media' },
        'ROLE_FLYPRO',
        api,
        auth.user ? false : true
      );

      if (therapistResponse && therapistResponse.length > 0) {
        setTherapist(therapistResponse[0]);
      }
    } catch (error) {
      console.error("Error while fetching therapist:", error);
    }
  };


  return (
    <div>
      <MenuHeader />
<div className="header details-booking-header">
  <button
    type="button"
    className="details-booking-back"
    onClick={handleGoBack}
  >
    {"<"} {t('back')}
  </button>

  <h1 className="details-booking-title">{t('order_details')}</h1>

  <div className="details-booking-spacer" aria-hidden="true" />
</div>

      <div className="form-details-booking">
        <div className="form-inscription details-book">
          <form className="booking-form">

            {/* Date */}
            <div className="form-text">
              <input
                type="text"
                readOnly
                value={t('date_label').padEnd(40) + formatDateToZurichTime(booking?.date, totalDuration, i18n.language)}
                className="spaced-input"
              />
            </div>

            {/* Status */}
            <div className="form-text">
              <input
                type="text"
                readOnly
                value={t('status_label').padEnd(40) + t(`status.${(booking?.status === 'DECLINED' ? 'PENDING' : booking?.status || 'ASSIGNED').toLowerCase()}`)}
                className="spaced-input"
              />
            </div>

            {/* flypro */}
            <div className="form-text flypro-book">
              <span style={{ marginLeft: '15px', whiteSpace: 'pre' }}>
                {t('flypro').padEnd(40)}
              </span>
              <img
                src={therapist?.media?.length ? therapist.media[0].original_url : "assets/images/icon-pro.svg"}
                alt="Therapist"
                style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '10px' }}
              />
              <span>{`${therapist?.firstname || ''} ${therapist?.lastname || ''}`}</span>
            </div>

            {/* Address */}
            <div className="form-text">
              <input
                type="text"
                readOnly
                value={t('address_label').padEnd(38) + `${booking?.address?.street || ''}, ${booking?.address?.street_number || ''}, ${booking?.address?.zip_code || ''}, ${booking?.address?.state || ''}`}
                className="spaced-input"
              />
            </div>

            {/* Message */}
            <div className="form-text">
              <input
                type="text"
                readOnly
                value={t('message_label').padEnd(35) + ` ${booking?.message || 'N/A'}`}
                className="spaced-input"
              />
            </div>

            {/* Treatment */}
            <div className="form-text">
              <input
                type="text"
                readOnly
                value={t('treatment_label').padEnd(35) + `${products?.map(
                  (prod) => `${prod?.category?.title || ''} - ${prod?.duration || 0} min`).join(', ')}`}
                className="spaced-input"
              />
            </div>

            {/* Duration */}
            <div className="form-text">
              <input
                type="text"
                readOnly
                value={t('duration_label').padEnd(35) + `${totalDuration} ${t('minutes')}`}
                className="spaced-input"
              />
            </div>

          </form>
        </div>

        <div className="map-book">
          {isMapLoaded ? (
            <Map
              coordinates={coordinates}
              routeMarkers={routeMarkers}
              therapist={therapist}
              address={booking.address}
            />
          ) : (
            <div className="map-container" role="status">
              {t(mapLoadError ? 'map_load_error' : 'map_loading')}
            </div>
          )}
        </div>
      </div>
    </div>
  );

};

export default DetailsBooking;
